import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { projectApi } from "@frontend/features/projects";
import type { ProjectRow } from "@frontend/features/projects";
import type { Reference } from "@frontend/lib/references";
import { loadReferences } from "@frontend/lib/references";

import type { AmendmentPerPage } from "../types";

export type AmendmentSubscriptionsState = {
  year: number;
  rows: ProjectRow[];
  total: number;
  page: number;
  perPage: AmendmentPerPage;
  search: string;
  responsable: number | null;
  responsables: Reference[];
  loading: boolean;
  error: string | null;
};

export const initialAmendmentSubscriptionsState: AmendmentSubscriptionsState = {
  year: new Date().getFullYear(),
  rows: [],
  total: 0,
  page: 1,
  perPage: 25,
  search: "",
  responsable: null,
  responsables: [],
  loading: true,
  error: null,
};

export const amendmentSubscriptionsAtom =
  atomWithImmer<AmendmentSubscriptionsState>(initialAmendmentSubscriptionsState);

export const amendmentSubscriptionsPageCountAtom = atom((get) => {
  const { total, perPage } = get(amendmentSubscriptionsAtom);

  return Math.max(1, Math.ceil(total / perPage));
});

/** Numbers the reads, so an answer to an older one is dropped. */
const drawAtom = atom(0);

/**
 * Two reads rather than one: the rows come from the project listing, which is
 * where a subscription lives, and the names of the responsable filter from the
 * project reference list, which caches them per year.
 */
export const loadAmendmentSubscriptionsAtom = atom(null, async (get, set) => {
  const draw = get(drawAtom) + 1;
  set(drawAtom, draw);

  const { year, search, responsable, page, perPage } = get(
    amendmentSubscriptionsAtom,
  );

  set(amendmentSubscriptionsAtom, (state) => {
    state.loading = true;
    state.error = null;
  });

  try {
    const [loaded, responsables] = await Promise.all([
      projectApi.listProjects({
        search,
        type: "subscription",
        // An archived subscription still owes the weeks of a year gone by, so
        // the listing is not narrowed to the active ones.
        filter: "all",
        page,
        perPage,
        closureWeeksYear: year,
        responsableId: responsable,
      }),
      loadReferences("projectAccountants", { closureWeeksYear: year }),
    ]);

    if (get(drawAtom) !== draw) {
      return;
    }

    set(amendmentSubscriptionsAtom, (state) => {
      state.rows = loaded.items;
      state.total = loaded.total;
      state.page = loaded.page;
      state.responsables = responsables;
      state.loading = false;
    });
  } catch (error: unknown) {
    if (get(drawAtom) !== draw) {
      return;
    }

    set(amendmentSubscriptionsAtom, (state) => {
      state.loading = false;
      state.error = error instanceof Error ? error.message : String(error);
    });
  }
});

/** A new filter always sends the reader back to the first page. */
export const setAmendmentSubscriptionsSearchAtom = atom(
  null,
  (_get, set, search: string) => {
    set(amendmentSubscriptionsAtom, (state) => {
      state.search = search;
      state.page = 1;
    });
    void set(loadAmendmentSubscriptionsAtom);
  },
);

export const setAmendmentSubscriptionsResponsableAtom = atom(
  null,
  (_get, set, responsable: number | null) => {
    set(amendmentSubscriptionsAtom, (state) => {
      state.responsable = responsable;
      state.page = 1;
    });
    void set(loadAmendmentSubscriptionsAtom);
  },
);

export const setAmendmentSubscriptionsPerPageAtom = atom(
  null,
  (_get, set, perPage: AmendmentPerPage) => {
    set(amendmentSubscriptionsAtom, (state) => {
      state.perPage = perPage;
      state.page = 1;
    });
    void set(loadAmendmentSubscriptionsAtom);
  },
);

export const setAmendmentSubscriptionsPageAtom = atom(
  null,
  (_get, set, page: number) => {
    set(amendmentSubscriptionsAtom, (state) => {
      state.page = page;
    });
    void set(loadAmendmentSubscriptionsAtom);
  },
);

export const setAmendmentYearAtom = atom(null, (_get, set, year: number) => {
  set(amendmentSubscriptionsAtom, (state) => {
    state.year = year;
    state.page = 1;
  });
  void set(loadAmendmentSubscriptionsAtom);
});
